import { state } from '../map_state/state'
import { viewport } from '../map_state/viewport'
import { Renderer } from '../renderer'
import { callMethodOnKeydown, getCanvasCursorPosition, isTyping, screenCssToWorld } from '../helpers'
import { Sensor } from '../models/sensor'
import { loader } from '../map_state/loader'
import { panTool } from './pan_tool'
import { cropTool } from './crop_tool'
import { dragTool } from './drag_tool'
import { linearFlowTool } from './linear_flow_tool'
import { pathTracerTool } from './path_tracer_tool'
import { historyTool } from './history_tool'

class AddSensorTool {
  constructor () {
    this.element = document.getElementById('mct-add-ex-sensor')
    this.tab = document.getElementById('mct-sensors-tab')
    this.keys = {
      Backspace: 'Remove previous placed sensor',
      Escape: 'Remove all sensors'
    }
    this.previewFrameRequested = false
    this.pendingPreviewPointer = null
    this.toggleVisibility()
    this.bindEvents()
  }

  bindEvents () {
    this.onKeyDown = this.onKeyDown.bind(this)
    this.activate = this.activate.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerLeave = this.onPointerLeave.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onSensorPointerDown = this.onSensorPointerDown.bind(this)
    this.onSensorPointerUp = this.onSensorPointerUp.bind(this)
    this.onSensorPointerMove = this.onSensorPointerMove.bind(this)
    this.element.addEventListener('click', this.activate)
    this.flushPreviewFrame = this.flushPreviewFrame.bind(this)
    document.addEventListener('keydown', this.onKeyDown)
    setTimeout(() => {
      viewport.canvas.addEventListener('pointerup', this.onSensorPointerUp)
      viewport.canvas.addEventListener('pointerdown', this.onSensorPointerDown)
      viewport.canvas.addEventListener('pointermove', this.onSensorPointerMove)
    }, 1000)
  }

  unBindEvents () {
    this.element.removeEventListener('click', this.activate)
    document.removeEventListener('keydown', this.onKeyDown)
    viewport.canvas.removeEventListener('pointerup', this.onSensorPointerUp)
    viewport.canvas.removeEventListener('pointerdown', this.onSensorPointerDown)
    viewport.canvas.removeEventListener('pointermove', this.onSensorPointerMove)
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key === 'a' && !this.element.classList.contains('hidden')) callMethodOnKeydown(e.target, this.activate)
    if (e.key === 'Escape' && state.addSensorMode) {
      state.sensors = []
      this.tab.innerHTML = ''
      historyTool.add()
      Renderer.render()
    }
    if (e.key === 'Backspace' && state.addSensorMode) {
      state.sensors[state.sensors.length - 1].remove()
      historyTool.add()
      Renderer.render()
    }
  }

  activate (mode = true) {
    if (!state.mapImage) return

    this.active = !state.addSensorMode && mode
    if (this.active) {
      if (state.panMode) panTool.activate(false)
      else if (state.cropMode) cropTool.activate(false)
      else if (state.dragMode) dragTool.activate(false)
      else if (state.linearFlowMode) linearFlowTool.activate(false)
      else if (state.tracerMode) pathTracerTool.enableMode(state.tracerMode, false)
      this.element.classList.add('selected')
      state.addSensorMode = true
      viewport.canvas.addEventListener('click', this.onClick)
      viewport.canvas.addEventListener('pointermove', this.onPointerMove)
      viewport.canvas.addEventListener('pointerleave', this.onPointerLeave)
    } else {
      state.previewSensor = null
      this.pendingPreviewPointer = null
      this.previewFrameRequested = false
      state.addSensorMode = false
      this.element.classList.remove('selected')
      viewport.canvas.removeEventListener('click', this.onClick)
      viewport.canvas.removeEventListener('pointermove', this.onPointerMove)
      viewport.canvas.removeEventListener('pointerleave', this.onPointerLeave)
    }

    Renderer.render()
  }

  toggleVisibility () {
    this.element.classList.toggle('hidden', !state.mapImage)
  }

  onClick (e) {
    if (!state.addSensorMode) return false
    if (!state.mapImage) return false

    const [world, _aabb, inside] = getCanvasCursorPosition(e.clientX, e.clientY)
    if (!inside) {
      loader.setStatusMessage('failure', 'Sensor must be inside map.')
      return true
    }

    const id = state.nextSensorId
    const sensor = new Sensor({
      id,
      name: `Sensor ${id}`,
      position: world
    })
    sensor.ensureDefaults()
    state.canEditMap = false

    state.sensors = [...state.sensors, sensor]
    state.nextSensorId += 1
    this.tab.appendChild(sensor.element)
    sensor.select()
    Sensor.updateNames()
    historyTool.add()
    Renderer.render()
  }

  onPointerLeave () {
    this.pendingPreviewPointer = null

    if (state.addSensorMode && state.previewSensor) {
      state.previewSensor = null
      Renderer.render({ syncUi: false })
    }
  }

  onPointerMove (e) {
    if (!state.addSensorMode) return
    if (!state.mapImage) return

    this.pendingPreviewPointer = {
      clientX: e.clientX,
      clientY: e.clientY
    }

    if (this.previewFrameRequested) return

    this.previewFrameRequested = true
    requestAnimationFrame(this.flushPreviewFrame)
  }

  flushPreviewFrame () {
    this.previewFrameRequested = false

    if (!state.addSensorMode) return
    if (!state.mapImage) return
    if (!this.pendingPreviewPointer) return

    const { clientX, clientY } = this.pendingPreviewPointer
    const [world, _aabb, inside] = getCanvasCursorPosition(clientX, clientY)

    if (!inside) {
      if (state.previewSensor) {
        state.previewSensor = null
        Renderer.render({ syncUi: false })
      }
      return
    }

    state.cursorWorld = world

    if (!state.previewSensor) {
      state.previewSensor = new Sensor({
        id: 'preview',
        name: 'Preview',
        position: world
      })
      state.previewSensor.ensureDefaults()
    } else {
      state.previewSensor.position = world
    }

    Renderer.render({ syncUi: false })
  }

  onSensorPointerMove (e) {
    if (!state.zoneDrag) return

    const { sensor, which } = state.zoneDrag

    if ((which === 'A' && !sensor.enableZoneA) || (which === 'B' && !sensor.enableZoneB)) {
      state.zoneDrag = null
      viewport.canvas.releasePointerCapture(e.pointerId)
      Renderer.render()
      return
    }

    const rect = viewport.canvas.getBoundingClientRect()
    const world = screenCssToWorld({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    const rWorldPx = Math.hypot(world.x - sensor.position.x, world.y - sensor.position.y)

    sensor.setZoneFromWorldPx(which, rWorldPx)

    Renderer.render({ syncUi: false })
  }

  onSensorPointerDown (e) {
    if (!state.mapImage) return
    if (state.addSensorMode) return
    if (state.pointDistanceMode) return
    if (state.linearFlowMode) return
    if (state.dragMode) return

    const rect = viewport.canvas.getBoundingClientRect()
    const world = screenCssToWorld({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    const hit = this.findSensorUnderCursor(world)

    if (!hit) {
      document.querySelector(`[data-sensor-id='${state.selectedSensorId}']`)?.classList?.remove('selected')
      state.selectedSensorId = null
      Renderer.render()
      return
    }

    hit.select()

    const which = this.hitTestZoneFill(hit, world)
    if (!which) {
      Renderer.render()
      return
    }

    e.preventDefault()
    e.stopPropagation()
    state.zoneDrag = { sensor: hit, which }
    viewport.canvas.setPointerCapture(e.pointerId)
    Renderer.render()
  }

  findSensorUnderCursor (world) {
    let best = null
    let bestScore = Infinity

    const tolWorld = state.zoneHitTolerancePx / state.zoomValue
    const dotWorld = 6 / state.zoomValue

    for (const s of state.sensors) {
      const d = Math.hypot(world.x - s.position.x, world.y - s.position.y)

      const { a, b } = s.getZonePx()
      const rPick = (b != null ? b : a)

      const insideZone = (rPick != null) && d <= rPick + tolWorld
      const onDot = d <= dotWorld

      if ((insideZone || onDot) && d < bestScore) {
        best = s
        bestScore = d
      }
    }

    return best
  }

  hitTestZoneFill (sensor, world) {
    const { a, b } = sensor.getZonePx(state.lenPath)
    const d = Math.hypot(world.x - sensor.position.x, world.y - sensor.position.y)

    if (a != null && d <= a) return 'A'
    if (b != null && d <= b) return 'B'
    return null
  }

  onSensorPointerUp (e) {
    if (!state.zoneDrag) return
    state.zoneDrag = null
    viewport.canvas.releasePointerCapture(e.pointerId)
    historyTool.add()
    Renderer.render()
  }

  rebuildTab () {
    this.tab.innerHTML = ''

    state.sensors.forEach(sensor => {
      sensor.element.classList.remove('selected')
      this.tab.appendChild(sensor.element)
    })

    if (state.selectedSensorId != null) document.querySelector(`[data-sensor-id='${state.selectedSensorId}']`)?.classList?.add('selected')
  }

  syncVisibleRSSI () {
    if (!this.tab) return

    this.tab.querySelectorAll('.mct-zone-rssi').forEach(input => {
      input.closest('.mct-zone-field-label')?.classList.toggle('hidden', !state.visibleRSSI)
    })
  }
}

export let addSensorTool = null

export function initAddSensorTool () {
  addSensorTool = new AddSensorTool()
  return addSensorTool
}
